import { useState, useEffect } from "react";
import { Youtube, Instagram, Facebook } from "lucide-react";
import { AnimateOnScroll, SectionLabel, SectionHeading, ScrollToTop } from "@/components/shared";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.77 1.52V6.96a4.84 4.84 0 0 1-1.01-.27z"/></svg>
);

const PlatformIcon = ({ icon, size = 22 }) => {
  switch (icon) { case "youtube": return <Youtube size={size} />; case "tiktok": return <TikTokIcon />; case "instagram": return <Instagram size={size} />; default: return null; }
};

export default function About() {
  ScrollToTop();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/content/about`).then(r => r.json()).then(setData).catch(() => {});
  }, []);

  if (!data) return <main className="pt-24 pb-24 bg-[#050505] min-h-screen" />;

  return (
    <main data-testid="about-page" className="pt-24 md:pt-28 pb-24 bg-[#050505]">
      <section className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <AnimateOnScroll>
            <div className="relative">
              <div className="aspect-[3/4] overflow-hidden border border-white/5">
                <img src={data.portrait} alt="George Emad" className="w-full h-full object-cover" data-testid="about-portrait" />
              </div>
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border-r-2 border-b-2 border-[#E63946]/30" />
            </div>
          </AnimateOnScroll>
          <AnimateOnScroll delay={0.15}>
            <div>
              <SectionLabel text="About" />
              <h1 className="mt-3 font-heading text-4xl md:text-5xl font-black tracking-tighter text-white leading-tight">George Emad</h1>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#E63946]">Tech Content Creator & Video Producer</p>
              <p className="mt-6 text-base text-zinc-400 leading-relaxed">{data.bio}</p>
              <p className="mt-4 text-base text-zinc-400 leading-relaxed">{data.style}</p>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      <div className="mt-24 py-6 border-y border-white/5 overflow-hidden">
        <Marquee speed={30} gradient={false}>
          {["CONTENT CREATOR", "VIDEO PRODUCER", "TECH ENTHUSIAST", "BRAND PARTNER", "STORYTELLER", "REVIEWER"].map((t, i) => (
            <span key={i} className="mx-10 font-heading text-5xl md:text-7xl font-black text-transparent tracking-tighter" style={{ WebkitTextStroke: "1px rgba(255,255,255,0.06)" }}>{t}</span>
          ))}
        </Marquee>
      </div>

      <section className="mt-24 max-w-7xl mx-auto px-6 lg:px-8">
        <AnimateOnScroll><SectionLabel text="Journey" /><SectionHeading>The Story So Far</SectionHeading></AnimateOnScroll>
        <div className="mt-12 relative">
          <div className="absolute left-[7px] md:left-1/2 top-0 bottom-0 w-[1px] bg-white/10 md:-translate-x-[0.5px]" />
          {(data.journey || []).map((item, i) => (
            <AnimateOnScroll key={i} delay={i * 0.08}>
              <div className={`relative flex items-start gap-6 md:gap-0 mb-10 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-[15px] h-[15px] rounded-full bg-[#050505] border-2 border-[#E63946] z-10" />
                <div className={`ml-8 md:ml-0 md:w-1/2 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12 md:text-left"}`}>
                  <span className="font-mono text-xs text-[#E63946]">{item.year}</span>
                  <p className="mt-1 text-sm text-zinc-300">{item.milestone}</p>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </section>

      <section className="mt-24 py-24 bg-[#0F0F11]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimateOnScroll><SectionLabel text="Platforms" /><SectionHeading>Where I Create</SectionHeading></AnimateOnScroll>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(data.platforms || []).map((p, i) => (
              <AnimateOnScroll key={i} delay={i * 0.1}>
                <div data-testid={`about-platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`} className="p-6 bg-[#050505] border border-white/5 hover:border-[#E63946]/20 transition-all duration-300 group">
                  <span className="text-zinc-500 group-hover:text-[#E63946] transition-colors block mb-4"><PlatformIcon icon={p.icon} size={28} /></span>
                  <h4 className="font-heading text-base font-semibold text-white">{p.name}</h4>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{p.description}</p>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
